import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import LookCanvas from './LookCanvas';
import LookTab from './LookTab';
import LayersTab from './LayersTab';
import TextTab from './TextTab';
import GuidesTab from './GuidesTab';
import LayerPanel, { syncLayerOrder } from './LayerPanel';
import ProofPanel from './ProofPanel';
import useUndoRedo from './useUndoRedo';
import { newTextField } from './textFieldDefaults';
import { createLook, updateLook, fetchReferencePosesForSeason } from '../shared/api';
import { invalidateThumbnails } from '../shared/thumbnailCache';
import generateThumbnail from '../shared/generateThumbnail';
import { InlineSpinner } from '../shared/Spinner';

const PANEL_TABS = ['Look', 'Guides', 'Layers', 'Text'];

const SAMPLE_VARIABLES = {
  first_name: 'Emily',
  last_name: 'Johnson',
  grade: '5th',
  school_name: 'Lincoln Elementary',
};

const defaultLook = {
  name: '',
  season: '',
  pose_number: '-01',
  look_type: 'single',
  canvas_width: 2400,
  canvas_height: 3000,
  canvas_dpi: 300,
  eyes_y: null,
  face_height: null,
  center_x: null,
  rotation: 0,
  source_if_3: null,
  source_if_2: null,
  source_if_1: null,
  canvas_filepath: null,
  canvas_filename: null,
  canvas_bg_ref_data: null,
  collage_photos: [
    {
      look_ref: null, season: null, x: null, y: null, width: null, height: null,
    },
    {
      look_ref: null, season: null, x: null, y: null, width: null, height: null,
    },
  ],
  overlay_s3_path: null,
  overlay_filename: null,
  mascot_center_x: null,
  mascot_center_y: null,
  mascot_size: null,
  layer_order: [],
  ref_photo_data: null,
  ref_zoom: 1,
  ref_rotation: 0,
  ref_offset_x: 0,
  ref_offset_y: 0,
  text_fields: [],
  promo_gif_campaign_id: null,
};

const LookEditor = ({
  look, looks: allLooks, referencePoses: externalPoses, onSaved, onBack, onDirtyChange,
}) => {
  const [activePanel, setActivePanel] = useState('Look');
  const {
    value: formData, setValue: setFormData,
    previewValue, commitPreview,
    undo, redo, canUndo, canRedo, reset,
  } = useUndoRedo(look || defaultLook);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  // A canvas change that would reshape the campaign's artwork: the server
  // holds the save and asks. `confirmAspectChange` is the answer, and it
  // re-runs the same save (see the effect below).
  const [aspectWarning, setAspectWarning] = useState(null);
  const [confirmAspectChange, setConfirmAspectChange] = useState(false);
  const [notice, setNotice] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedElement, setSelectedElement] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [sampleData, setSampleData] = useState({ ...SAMPLE_VARIABLES });
  // Preview text at font_size_max (default) or font_size_min so admins can
  // sanity-check both extremes of the shrink-to-fit range against a long name.
  const [textSizePreview, setTextSizePreview] = useState('max');
  const removedFieldIds = useRef([]);
  const clipboardRef = useRef(null);
  const [localPoses, setLocalPoses] = useState([]);

  // Use poses from the Layout tab if available, otherwise fetch by season
  const referencePoses = (externalPoses && externalPoses.some(Boolean)) ? externalPoses : localPoses;

  useEffect(() => {
    reset(look || defaultLook);
    setDirty(false);
    setError(null);
    // Both belong to the look you just left: a resize message and an unanswered
    // reshape prompt would otherwise follow you onto the next slide.
    setNotice(null);
    setAspectWarning(null);
    setValidationErrors({});
    removedFieldIds.current = [];
  }, [look]);

  // Fetch reference poses by season when the Layout tab hasn't provided them
  useEffect(() => {
    if (externalPoses && externalPoses.some(Boolean)) return;
    const season = look?.season;
    if (!season) { setLocalPoses([]); return; }
    fetchReferencePosesForSeason(season)
      .then((poses) => setLocalPoses(poses || []))
      .catch(() => setLocalPoses([]));
  }, [look?.season, externalPoses]);

  // Report dirty state to parent
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handler = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);

  // Delete/Backspace to remove selected collage photo
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Don't trigger when typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!selectedElement || selectedElement.type !== 'collage_photo') return;
      if (formData.look_type !== 'collage' && formData.look_type !== 'gif_slide') return;

      e.preventDefault();
      const idx = selectedElement.index;
      const photos = formData.collage_photos || [];
      if (idx < 0 || idx >= photos.length) return;

      setFormData((prev) => ({
        ...prev,
        collage_photos: prev.collage_photos.filter((_, i) => i !== idx),
        _selectedCollagePhoto: null,
      }));
      setSelectedElement(null);
      setDirty(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedElement, formData.look_type, formData.collage_photos]);

  // Copy/Paste for text fields and collage photos
  useEffect(() => {
    const handler = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

      // Copy
      if (e.key === 'c' && selectedElement) {
        e.preventDefault();
        if (selectedElement.type === 'text' && selectedElement.index != null) {
          const tf = (formData.text_fields || [])[selectedElement.index];
          if (tf) clipboardRef.current = { type: 'text', data: { ...tf, id: undefined } };
        } else if (selectedElement.type === 'collage_photo' && selectedElement.index != null) {
          const photo = (formData.collage_photos || [])[selectedElement.index];
          if (photo) clipboardRef.current = { type: 'collage_photo', data: { ...photo } };
        } else if (selectedElement.type === 'face') {
          clipboardRef.current = {
            type: 'face',
            data: {
              eyes_y: formData.eyes_y, face_height: formData.face_height, center_x: formData.center_x, rotation: formData.rotation,
            },
          };
        } else if (selectedElement.type === 'mascot') {
          clipboardRef.current = {
            type: 'mascot',
            data: { mascot_center_x: formData.mascot_center_x, mascot_center_y: formData.mascot_center_y, mascot_size: formData.mascot_size },
          };
        }
      }

      // Paste
      if (e.key === 'v' && clipboardRef.current) {
        e.preventDefault();
        const clip = clipboardRef.current;
        const OFFSET = 0.03; // slight offset so pasted element is visible

        if (clip.type === 'text') {
          const newField = {
            ...clip.data,
            id: undefined,
            position: (formData.text_fields || []).length,
            center_x: Math.min(1, (clip.data.center_x || 0.5) + OFFSET),
            center_y: Math.min(1, (clip.data.center_y || 0.5) + OFFSET),
          };
          setFormData((prev) => ({
            ...prev,
            text_fields: [...(prev.text_fields || []), newField],
          }));
          setDirty(true);
        } else if (clip.type === 'collage_photo' && (formData.look_type === 'collage' || formData.look_type === 'gif_slide')) {
          const newPhoto = {
            ...clip.data,
            x: Math.min(1, (clip.data.x || 0) + OFFSET),
            y: Math.min(1, (clip.data.y || 0) + OFFSET),
          };
          setFormData((prev) => ({
            ...prev,
            collage_photos: [...(prev.collage_photos || []), newPhoto],
          }));
          setDirty(true);
        } else if (clip.type === 'face') {
          setFormData((prev) => ({
            ...prev,
            eyes_y: Math.min(1, (clip.data.eyes_y || 0.3) + OFFSET),
            face_height: clip.data.face_height,
            center_x: Math.min(1, (clip.data.center_x || 0.5) + OFFSET),
            rotation: clip.data.rotation,
          }));
          setDirty(true);
        } else if (clip.type === 'mascot') {
          setFormData((prev) => ({
            ...prev,
            mascot_center_x: Math.min(1, (clip.data.mascot_center_x || 0.5) + OFFSET),
            mascot_center_y: Math.min(1, (clip.data.mascot_center_y || 0.5) + OFFSET),
            mascot_size: clip.data.mascot_size,
          }));
          setDirty(true);
        }
      }

      // Duplicate (Ctrl+D) — copy and paste in one step
      if (e.key === 'd' && selectedElement) {
        e.preventDefault();
        if (selectedElement.type === 'text' && selectedElement.index != null) {
          const tf = (formData.text_fields || [])[selectedElement.index];
          if (tf) {
            const newField = {
              ...tf,
              id: undefined,
              position: (formData.text_fields || []).length,
              center_x: Math.min(1, (tf.center_x || 0.5) + OFFSET),
              center_y: Math.min(1, (tf.center_y || 0.5) + OFFSET),
            };
            setFormData((prev) => ({
              ...prev,
              text_fields: [...(prev.text_fields || []), newField],
            }));
            setDirty(true);
          }
        } else if (selectedElement.type === 'collage_photo' && selectedElement.index != null && (formData.look_type === 'collage' || formData.look_type === 'gif_slide')) {
          const photo = (formData.collage_photos || [])[selectedElement.index];
          if (photo) {
            const newPhoto = {
              ...photo,
              x: Math.min(1, (photo.x || 0) + OFFSET),
              y: Math.min(1, (photo.y || 0) + OFFSET),
            };
            setFormData((prev) => ({
              ...prev,
              collage_photos: [...(prev.collage_photos || []), newPhoto],
            }));
            setDirty(true);
          }
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedElement, formData]);

  const update = (changes) => {
    setFormData((prev) => ({ ...prev, ...changes }));
    setDirty(true);
  };

  const updateTextField = (index, changes) => {
    setFormData((prev) => {
      const fields = [...(prev.text_fields || [])];
      fields[index] = { ...fields[index], ...changes };
      return { ...prev, text_fields: fields };
    });
    setDirty(true);
  };

  // New-field defaults depend on canvas orientation (see textFieldDefaults.js):
  // a portrait default lands across the photo on a landscape collage look.
  const addTextField = () => {
    setFormData((prev) => ({
      ...prev,
      text_fields: [
        ...(prev.text_fields || []),
        newTextField((prev.text_fields || []).length, prev.canvas_width, prev.canvas_height),
      ],
    }));
    setDirty(true);
  };

  const removeTextField = (index) => {
    setFormData((prev) => {
      const removed = prev.text_fields[index];
      if (removed?.id) removedFieldIds.current.push(removed.id);
      return {
        ...prev,
        text_fields: prev.text_fields.filter((_, i) => i !== index)
          .map((tf, i) => ({ ...tf, position: i })),
      };
    });
    setDirty(true);
  };

  // Canvas drag: live preview without history push
  const handleCanvasChange = useCallback((newFormData) => {
    previewValue(newFormData);
    setDirty(true);
  }, [previewValue]);

  // Canvas drag end: commit to undo history
  const handleCanvasCommit = useCallback(() => {
    commitPreview();
  }, [commitPreview]);

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. If you leave, your changes will be lost.')) {
      return;
    }
    onBack();
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Required';
    if (!formData.season) errs.season = 'Required';
    if (!formData.canvas_width) errs.canvas_width = 'Required';
    if (!formData.canvas_height) errs.canvas_height = 'Required';
    if (!formData.canvas_dpi) errs.canvas_dpi = 'Required';

    // Face/eye data is only required for single looks -- collages and gif
    // slides place photos explicitly and don't crop-to-face themselves.
    if (formData.look_type === 'single') {
      if (formData.eyes_y == null) errs.eyes_y = 'Required';
      if (formData.face_height == null) errs.face_height = 'Required';
      if (formData.center_x == null) errs.center_x = 'Required';
      if (formData.source_if_3 == null && formData.source_if_2 == null && formData.source_if_1 == null) {
        errs.source_if_3 = 'At least one source required';
        errs.source_if_2 = 'At least one source required';
        errs.source_if_1 = 'At least one source required';
      }
    }

    if (formData.look_type === 'gif_slide' && !formData.promo_gif_campaign_id) {
      errs.promo_gif_campaign_id = 'Pick a campaign';
    }

    // Unique name check
    if (formData.name && allLooks) {
      const dup = allLooks.find((l) => l.name === formData.name && l.id !== formData.id);
      if (dup) errs.name = 'This name is already in use';
    }

    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    setValidationErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please fix the highlighted fields before saving.');
      // Navigate to the tab containing the first error
      const guidesFields = ['eyes_y', 'face_height', 'center_x'];
      const hasGuidesError = guidesFields.some((f) => errs[f]);
      const lookFields = ['name', 'season', 'canvas_width', 'canvas_height', 'canvas_dpi', 'source_if_3', 'source_if_2', 'source_if_1', 'promo_gif_campaign_id'];
      const hasLookError = lookFields.some((f) => errs[f]);
      if (hasLookError) setActivePanel('Look');
      else if (hasGuidesError) setActivePanel('Guides');
      return;
    }

    setSaving(true);
    setError(null);
    // Same lifetime as `error`: the resize message describes THIS save, and a
    // stale one left on screen would credit the next save with a campaign-wide
    // change that never happened.
    setNotice(null);

    try {
      const textFieldAttrs = (formData.text_fields || []).map((tf, i) => ({
        id: tf.id || undefined,
        position: i,
        content: tf.content,
        font_s3_path: tf.font_s3_path,
        font_filename: tf.font_filename,
        font_color: tf.font_color,
        font_size_min: tf.font_size_min,
        font_size_max: tf.font_size_max,
        center_x: tf.center_x,
        center_y: tf.center_y,
        bound_x_left: tf.bound_x_left,
        bound_x_right: tf.bound_x_right,
        sizing_rule: tf.sizing_rule,
        align: tf.align,
        bold: tf.bold,
        italic: tf.italic,
        shadow: tf.shadow,
      }));

      // Include _destroy entries for removed text fields
      removedFieldIds.current.forEach((id) => {
        textFieldAttrs.push({ id, _destroy: true });
      });

      const payload = {
        ...formData,
        look_text_fields_attributes: textFieldAttrs,
      };

      // Regenerate thumbnail when the ref photo has changed
      if (payload.ref_photo_data) {
        try {
          payload.ref_photo_thumb = await generateThumbnail(payload.ref_photo_data);
        } catch (_) { /* keep existing thumb if generation fails */ }
      }

      // Remove keys that shouldn't be sent to the API
      delete payload.text_fields;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload._selectedCollagePhoto;

      const isNew = !formData.id;
      let saved;
      if (formData.id) {
        saved = await updateLook(formData.id, payload, { confirmAspectChange });
      } else {
        saved = await createLook(payload);
      }
      // A canvas change on a gif slide moves the whole campaign, so say what
      // happened — the designer edited one slide and three changed.
      if (saved.resized_slides > 1) {
        setNotice(`Canvas applied to all ${saved.resized_slides} slides in this campaign.`);
      }

      removedFieldIds.current = [];
      if (saved.id) invalidateThumbnails(saved.id);
      reset(saved);
      setDirty(false);
      setValidationErrors({});
      onSaved(saved, isNew);
    } catch (err) {
      // The server refuses a canvas change that would stretch the campaign's
      // artwork into a new shape, and asks rather than blocks: a deliberate
      // re-art is legitimate, an accidental squash is not.
      if (err.response?.data?.aspect_confirmation_required) {
        setAspectWarning(err.response.data.errors?.join(' ') || 'This would reshape the artwork.');
      } else {
        setError(err.response?.data?.errors?.join(', ') || err.message);
      }
    } finally {
      // The confirmation is spent whichever way the save went. Left set after a
      // failure it would silently pre-approve the NEXT canvas change, so the
      // reshape prompt the designer is meant to see would never appear again.
      setConfirmAspectChange(false);
      setSaving(false);
    }
  };

  // "Change anyway" on the reshape prompt: re-run the same save with the
  // designer's confirmation attached. The flag drives an effect rather than
  // calling handleSave directly, so the retry always reads the flag as set —
  // a direct call would race its own state update.
  const saveConfirmingAspect = () => {
    setAspectWarning(null);
    setConfirmAspectChange(true);
  };

  // handleSave is redefined every render, so it is deliberately not a
  // dependency — the flag is the trigger, and listing handleSave would re-run
  // the save on every keystroke.
  useEffect(() => {
    if (confirmAspectChange) handleSave();
  }, [confirmAspectChange]);

  return (
    <div style={{
      display: 'flex', flex: 1, width: '100%', minHeight: 0,
    }}
    >
      {/* Left panel */}
      <div style={{
        width: '38%',
        minWidth: 320,
        maxWidth: 480,
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        background: '#fafafa',
        flexShrink: 0,
        minHeight: 0,
      }}
      >
        {/* Panel tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
          {PANEL_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActivePanel(tab)}
              style={{
                flex: 1,
                padding: '8px 4px',
                border: 'none',
                borderBottom: activePanel === tab ? '2px solid #1976d2' : '2px solid transparent',
                background: 'none',
                fontSize: 12,
                fontWeight: activePanel === tab ? 600 : 400,
                color: activePanel === tab ? '#1976d2' : '#888',
                cursor: 'pointer',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Panel content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {activePanel === 'Look' && (
            <LookTab
              formData={formData}
              onChange={update}
              validationErrors={validationErrors}
              allLooks={allLooks}
              referencePoses={referencePoses}
            />
          )}
          {activePanel === 'Layers' && (
            <>
              <LayersTab formData={formData} onChange={update} />
              <div style={{ borderTop: '1px solid #e0e0e0', marginTop: 16, paddingTop: 16 }}>
                <LayerPanel
                  formData={formData}
                  onChange={update}
                  selectedElement={selectedElement}
                />
              </div>
            </>
          )}
          {activePanel === 'Text' && (
            <TextTab
              textFields={formData.text_fields || []}
              onUpdate={updateTextField}
              onAdd={addTextField}
              onRemove={removeTextField}
            />
          )}
          {activePanel === 'Guides' && (
            <GuidesTab formData={formData} onChange={update} validationErrors={validationErrors} />
          )}
        </div>

        {/* Save bar with undo/redo */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
        >
          <button
            onClick={handleBack}
            style={{
              padding: '8px 16px',
              background: '#eee',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Back
          </button>
          <button
            onClick={undo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            style={{
              padding: '6px 10px',
              background: canUndo ? '#e3f2fd' : '#f5f5f5',
              border: `1px solid ${canUndo ? '#90caf9' : '#e0e0e0'}`,
              borderRadius: 4,
              cursor: canUndo ? 'pointer' : 'default',
              fontSize: 13,
              color: canUndo ? '#1565c0' : '#bbb',
              lineHeight: 1,
            }}
          >
            ↶
          </button>
          <button
            onClick={redo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            style={{
              padding: '6px 10px',
              background: canRedo ? '#e3f2fd' : '#f5f5f5',
              border: `1px solid ${canRedo ? '#90caf9' : '#e0e0e0'}`,
              borderRadius: 4,
              cursor: canRedo ? 'pointer' : 'default',
              fontSize: 13,
              color: canRedo ? '#1565c0' : '#bbb',
              lineHeight: 1,
            }}
          >
            ↷
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            style={{
              flex: 1,
              padding: '8px 16px',
              background: dirty ? '#1976d2' : '#bbb',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: dirty ? 'pointer' : 'default',
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            {saving ? (
              <>
                <InlineSpinner size={14} color="#fff" />
                {' '}
                Saving...
              </>
            ) : formData.id ? 'Update Look' : 'Save New Look'}
          </button>
        </div>
        {error && (
          <div style={{ padding: '8px 16px', color: '#d32f2f', fontSize: 12 }}>{error}</div>
        )}

        {aspectWarning && (
          <div style={{
            margin: '0 16px 8px',
            padding: '10px 12px',
            border: '1px solid #e0b96a',
            background: '#fdf6e6',
            borderRadius: 3,
            fontSize: 12,
            color: '#7a5a12',
          }}
          >
            <div style={{ marginBottom: 8 }}>{aspectWarning}</div>
            <button
              type="button"
              onClick={saveConfirmingAspect}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                fontWeight: 600,
                border: '1px solid #b8860b',
                borderRadius: 3,
                background: '#fff',
                color: '#7a5a12',
                cursor: 'pointer',
                marginRight: 8,
              }}
            >
              Change anyway
            </button>
            <button
              type="button"
              onClick={() => setAspectWarning(null)}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                border: '1px solid #ccc',
                borderRadius: 3,
                background: '#fff',
                color: '#555',
                cursor: 'pointer',
              }}
            >
              Keep the current size
            </button>
          </div>
        )}

        {notice && (
          <div style={{ padding: '8px 16px', color: '#1b5e20', fontSize: 12 }}>{notice}</div>
        )}
      </div>

      {/* Canvas area */}
      <div style={{
        flex: 1, overflow: 'hidden', background: '#e8e8e8', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0,
      }}
      >
        {/* Simulation toolbar */}
        <div style={{
          padding: '8px 16px',
          background: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
        >
          <label style={{
            fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          }}
          >
            <input
              type="checkbox"
              checked={simulating}
              onChange={(e) => setSimulating(e.target.checked)}
            />
            Simulate Student Data
          </label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
            }}
          >
            <span style={{ color: '#666' }}>Preview text at:</span>
            {['max', 'min'].map((mode) => {
              const active = textSizePreview === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setTextSizePreview(mode)}
                  style={{
                    padding: '3px 10px',
                    fontSize: 11,
                    cursor: 'pointer',
                    border: `1px solid ${active ? '#1976d2' : '#ccc'}`,
                    background: active ? '#e3f2fd' : '#fff',
                    color: active ? '#0d47a1' : '#555',
                    borderRadius: 3,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {mode === 'max' ? 'Max size' : 'Min size'}
                </button>
              );
            })}
          </div>
          {simulating && (
            <>
              {Object.keys(SAMPLE_VARIABLES).map((key) => (
                <div key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <label style={{ fontSize: 10, color: '#666' }}>
                    {key.replace(/_/g, ' ')}
                    :
                  </label>
                  <input
                    type="text"
                    value={sampleData[key] || ''}
                    onChange={(e) => setSampleData((prev) => ({ ...prev, [key]: e.target.value }))}
                    style={{
                      width: 80,
                      padding: '2px 4px',
                      border: '1px solid #ccc',
                      borderRadius: 3,
                      fontSize: 11,
                    }}
                  />
                </div>
              ))}
            </>
          )}
        </div>
        <LookCanvas
          formData={formData}
          allLooks={allLooks}
          onChange={handleCanvasChange}
          onCommit={handleCanvasCommit}
          simulationData={simulating ? sampleData : null}
          onElementSelect={setSelectedElement}
          textSizePreview={textSizePreview}
        />
        {/* The canvas above is a preview, not the renderer. This is the real
            thing (#4559). Keyed on season so switching seasons drops a stale
            run instead of showing another season's proof. */}
        <ProofPanel
          key={formData.season}
          season={formData.season}
          lookTemplateId={formData.id}
        />
      </div>
    </div>
  );
};

export default LookEditor;
